import { useQuery } from '@apollo/client';
import { faEyeSlash, faPencil } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, Outlet, useParams } from 'react-router';
import invariant from 'tiny-invariant';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import {
  BookmarkParentType,
  BookmarkTargetType,
  ReviewCommentParentType,
  Role,
} from '~/apollo/generated/v3/graphql';
import { graphql } from '~/apollo/generated/v4';
import type { OutcropDetailPageQuery } from '~/apollo/generated/v4/graphql';
import { RoleSecured } from '~/components/auth/RoleSecured';
import { TargetBookmarksManagerModal } from '~/components/bookmark/TargetBookmarksManagerModal';
import { Heading } from '~/components/common/Heading';
import { NotFound } from '~/components/common/NotFound';
import { PageHeading } from '~/components/common/PageHeading';
import { Prose } from '~/components/common/Prose';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { GeologyTypeIconV4 } from '~/components/common/icons/geology-type-icon-v4';
import type { BreadcrumbItem } from '~/components/layout/Breadcrumb';
import { useBreadcrumb } from '~/components/layout/Breadcrumb';
import { TargetReviewCommentsModal } from '~/components/review-comment/TargetReviewCommentsModal';
import { Alert } from '~/components/ui/alert';
import {
  outcropRoute,
  regionRoute,
  searchRoute,
  uploadOutcropUpdateRoute,
} from '~/paths';
import { OutcropRouteNavigation } from '~/routes/outcrop/__OutcropRouteNavigation';
import { isPublished } from '~/utils/modules/supporting-object';

type Outcrop = NonNullable<OutcropDetailPageQuery['outcropGet']>;

const OUTCROP_DETAIL_PAGE = graphql(`
  query OutcropDetailPage($id: ID!) {
    outcropGet(id: $id) {
      ...outcropParts
      region {
        ...regionParts
      }
    }
  }
`);

function prependBreadcrumb(outcrop: Outcrop | null | undefined) {
  if (!outcrop) return null;

  const regionId = parseInt(outcrop.region.id);

  return [
    {
      routeId: searchRoute(),
      pathname: searchRoute(),
      title: 'Search Analogues',
    },
    {
      routeId: regionRoute(regionId),
      pathname: regionRoute(regionId),
      title: outcrop.region.name,
    },
  ] satisfies BreadcrumbItem[];
}

export default function OutcropDetailPage() {
  return (
    <ApolloProviderV4>
      <OutcropDetailPageInner />
    </ApolloProviderV4>
  );
}

function OutcropDetailPageInner() {
  const params = useParams();
  invariant(params.outcropId);
  const outcropId = parseInt(params.outcropId);

  const { data, loading } = useQuery(OUTCROP_DETAIL_PAGE, {
    variables: { id: outcropId },
  });

  const outcrop = data?.outcropGet;

  useBreadcrumb(
    'routes/outcrop/$outcropId',
    outcrop?.name ?? 'Outcrop',
    null,
    prependBreadcrumb(outcrop),
  );

  if (loading) return <SpinnerPlaceholder />;
  if (!outcrop) return <NotFound />;

  return (
    <>
      {!isPublished(outcrop) && (
        <Alert status="info" className="mb-4">
          <FontAwesomeIcon icon={faEyeSlash} className="text-xl" />
          <div>
            <Heading level={4}>Outcrop not published</Heading>
            <p>
              This outcrop has not been published and is only visible to admins.
            </p>
          </div>
        </Alert>
      )}

      <div className="float-right space-x-1">
        <TargetBookmarksManagerModal
          parentType={BookmarkParentType.Outcrop}
          parentId={outcropId}
          targetType={BookmarkTargetType.Outcrop}
          targetId={outcropId}
          path={outcropRoute(outcropId)}
        />

        <TargetReviewCommentsModal
          parentType={ReviewCommentParentType.Outcrop}
          parentId={parseInt(outcrop.id)}
          name={outcrop.name}
        />

        <RoleSecured roles={[Role.RoleAdmin]}>
          <Link
            to={uploadOutcropUpdateRoute(outcropId)}
            className="btn btn-sm btn-ghost gap-1"
          >
            <FontAwesomeIcon icon={faPencil} />
            Edit Outcrop
          </Link>
        </RoleSecured>
      </div>

      <PageHeading className="space-x-2">
        <span>{outcrop.name}</span>
        {outcrop.geologyType.map(gt => (
          <GeologyTypeIconV4
            key={gt}
            dominant={outcrop.dominantGeologyType === gt}
            geologyType={gt}
            className="w-8 h-8"
          />
        ))}
      </PageHeading>

      {outcrop.description && (
        <Prose dangerouslySetInnerHTML={{ __html: outcrop.description }} />
      )}
      <div className="clear-both" />

      <div className="grid md:grid-cols-12 gap-6">
        <div className="md:col-span-3 lg:col-span-2 pt-2">
          <OutcropRouteNavigation outcropId={outcropId} />
        </div>

        <div className="md:col-span-9 lg:col-span-10 pt-2">
          <Outlet />
        </div>
      </div>
    </>
  );
}
